import Trpc from 'digicel-kaizen-library';
import { DeviceEventEmitter, Platform } from 'react-native';
import TrpcApi from 'tripica-api';
import Uri from 'urijs';
import appConfig from '../config';
import AuthenticationTokenManager from './AuthenticationTokenManager';
import fetch from './fetch';
import requestCacheManager from './requestCacheManager';

export const getConfig = () => ({
  adaptorUrl: appConfig.ADAPTOR_URL,
  baseUrl: appConfig.API_URL,
  uiUrl: appConfig.UI_URL,
  channel: Platform.select({ ios: 'APP_IOS', android: 'APP_ANDROID', web: 'WEB_DESKTOP' }),
});

let tripicaBearerToken;

if (Platform.OS !== 'web') {
  AuthenticationTokenManager.get().then(token => (tripicaBearerToken = token));
}

const compareUrl = (url, urlsToCompare) => url && urlsToCompare.includes(new Uri(url).hostname());
const isTripicaApiUrl = url =>
  compareUrl(url, [new Uri(getConfig().baseUrl).hostname(), new Uri(getConfig().uiUrl).hostname()]);

export const fetchAdapter = (url, options = {}) => {
  const { body: optionBody, headers: optionHeaders, ...restOptions } = options;
  const headers = new Headers(optionHeaders);

  let body;
  if (optionBody) {
    body = JSON.stringify(optionBody);
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
  }

  if (isTripicaApiUrl(url)) {
    const cachedPromise = requestCacheManager.get(url, options);
    if (cachedPromise) {
      return cachedPromise;
    }

    if (Platform.OS !== 'web') {
      headers.append('Pragma', 'no-jwt-cookie');  // natif uniquement
    }
    headers.append('Cache-control', 'no-cache');

    if (tripicaBearerToken) {
      headers.append('Authorization', tripicaBearerToken);
    }
  }

  const resultPromise = fetch(url, {
    headers,
    body,
    ...restOptions,
  })
    .then(response => {
      if (!response.ok) {
        const bypassUnauthorizedCheck = options.context && options.context.bypassUnauthorizedRequestCheck;
        if (
          isTripicaApiUrl(response.url) &&
          (response.status === 401 || response.status === 403) &&
          !bypassUnauthorizedCheck
        ) {
          DeviceEventEmitter.emit('UNAUTHORIZED_REQUEST');
        }
        // Capture the temporary JWT from PIN_REQUIRED so the second OTP call
        // uses the same token as required by the triPica backend.
        // The capture is skipped when the caller sets context.skipOtpTokenCapture = true
        // (e.g. checkLogin in polling.js) to prevent background re-login attempts from
        // corrupting tripicaBearerToken with a temp JWT that subsequent calls cannot use.
        const skipOtpTokenCapture = options.context && options.context.skipOtpTokenCapture;
        if (!skipOtpTokenCapture && isTripicaApiUrl(response.url) && response.status === 401) {
          const tempToken = response.headers.get('Authorization');
          if (tempToken) {
            tripicaBearerToken = tempToken;
          }
        }

        return Promise.reject(response);
      }

      if (isTripicaApiUrl(response.url)) {
        const authHeaderContent = response.headers.get('Authorization');
        if (authHeaderContent && tripicaBearerToken !== authHeaderContent) {
          tripicaBearerToken = authHeaderContent;
          AuthenticationTokenManager.set(authHeaderContent);
          requestCacheManager.invalidate();
        }
      }

      return response.text();
    })
    .then(text => {
      try {
        return JSON.parse(text);
      } catch (err) {
        return null;
      }
    });

  if (isTripicaApiUrl(url)) {
    requestCacheManager.cache(resultPromise, url, options);
  }

  return resultPromise;
};

export const clearToken = () => {
  tripicaBearerToken = null;
  requestCacheManager.invalidate();
  return AuthenticationTokenManager.remove();
};

// Clears only the in-memory bearer token without touching the keychain or cache.
// Used before OTP resend so the API treats it as a fresh login (no temp JWT sent).
export const resetTripicaBearerToken = () => {
  tripicaBearerToken = null;
};

// Explicitly sets the in-memory bearer token and invalidates the request cache.
// Called in loginCall after a successful authentication (OTP or standard) to ensure
// tripicaBearerToken always reflects the final JWT. Without this, when OTP is active
// the temporary JWT from the PIN_REQUIRED 401 response stays in tripicaBearerToken
// after the second call succeeds (the success response Authorization header is absent
// or identical to the temp JWT, so the fetchAdapter success path does not update it),
// causing subsequent API calls such as fetchCustomerSaga to receive a 403.
export const setTripicaBearerToken = (token: string) => {
  tripicaBearerToken = token;
  requestCacheManager.invalidate();
};

export { AuthenticationTokenManager };

export default new TrpcApi(getConfig, fetchAdapter);

export const tripica = new Trpc(getConfig, fetchAdapter);
